import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBarSearchIcon } from 'assets/icons/NavBarSearchIcon'
import { FilterIcon } from 'assets/icons/FilterIcon'
import { SmallText } from 'styling/TypographyV2'
import { useDebounce } from 'hooks/useDebounde'
import { Pagination } from 'components/Pagination/Pagination'
import { CalendarInput } from 'components/CalendarInput/CalendarInput'
import {
  getSimpleOperationalTeams,
} from 'features/operationalManagement/operationalManagementSlice'
import { getUsers } from 'features/users(Employees)/usersSlice'
import { getDepartments } from 'features/departments/departmentsSlice'
import { getClientRoles } from 'features/roles/clientRolesSlice'
import { IAllEmployeesSingleResponse } from 'types/api/usersApi.interface'
import { ColorVariant, Flex } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { Option, Select } from 'components/Select/Select'
import { FilterIconButton, SearchField } from '../../Organogram.styled'
import { ORGANOGRAM_PAGE_LIMIT as PAGE_LIMIT } from '../../constants'
import {
  Checkbox,
  EmployeeListHeader,
  EmployeeListWrapper,
  EmployeeRow,
  FiltersBar,
  ModeCard,
  ModeCardIcon,
  ModeCardSubtitle,
  ModeCardTitle,
  ModePickerRow,
  SelectAllButton,
  SimpleFiltersBar,
  TeamChip,
} from '../BulkAssign.styled'

const FilterIconSvg = (
  <svg viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fill='currentColor'
      fillRule='evenodd'
      clipRule='evenodd'
      d='M3 4h14l-5.5 7v4.5L8.5 17v-6L3 4z'
    />
  </svg>
)

const ManualIconSvg = (
  <svg viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fill='currentColor'
      d='M8 9a3 3 0 100-6 3 3 0 000 6zm0 1.5c-3 0-6 1.5-6 4V17h9v-2c0-.8.2-1.5.7-2.1-1-.3-2-.4-3.7-.4zm10 3.5v-2h-2v-2h-1.5v2h-2v1.5h2v2H16v-2h2z'
    />
  </svg>
)

const ImportIconSvg = (
  <svg viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fill='currentColor'
      d='M10 2v9.6l3.3-3.3 1.4 1.4L10 14.4 5.3 9.7l1.4-1.4L10 11.6V2h0zM3 16h14v2H3v-2z'
    />
  </svg>
)

export type SelectionMode = 'filter' | 'manual' | 'import'

interface Props {
  mode: SelectionMode
  onModeChange: (m: SelectionMode) => void
  selected: Set<number>
  onChangeSelected: (next: Set<number>) => void
}

type Row = {
  id: number
  name: string
  empId: string
  roleName: string
  departmentName: string
  departmentId: number | null
  teamId: number | null
  teamName: string | null
}

const toRow = (u: IAllEmployeesSingleResponse): Row => {
  const contract = (u as any).contract ?? {}
  const department = contract.department ?? (u as any).department ?? null
  const team = contract.operationalTeam ?? (u as any).operationalTeam ?? null
  return {
    id: u.id,
    name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—',
    empId: `EMP-${String(u.id).padStart(3, '0')}`,
    roleName: (u as any).role?.name ?? '—',
    departmentName: department?.name ?? '—',
    departmentId: department?.id ?? null,
    teamId: team?.id ?? null,
    teamName: team?.name ?? null,
  }
}

export const SelectEmployeesStep = ({
  mode,
  onModeChange,
  selected,
  onChangeSelected,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>()

  const employees = useSelector(
    (state: RootState) => state.users.users,
  ) as unknown as IAllEmployeesSingleResponse[] | undefined
  const totalPages = useSelector(
    (state: RootState) => (state.users as any).totalPages ?? 0,
  )
  const isLoading = useSelector((state: RootState) => state.users.isLoading)

  const simpleTeams = useSelector(
    (state: RootState) => state.operationalManagement.simpleTeams,
  )
  const departments = useSelector(
    (state: RootState) => (state as any).departments?.departments,
  ) as any[] | undefined
  const clientRoles = useSelector(
    (state: RootState) => (state as any).clientRoles?.roles,
  ) as any[] | undefined

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)
  const [page, setPage] = useState(1)
  const [departmentId, setDepartmentId] = useState<number | null>(null)
  const [roleId, setRoleId] = useState<number | null>(null)
  const [teamId, setTeamId] = useState<number | null>(null)
  const [rangeStart, setRangeStart] = useState<Date | null>(null)
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null)

  useEffect(() => {
    dispatch(getSimpleOperationalTeams(undefined))
    dispatch(getDepartments({ page: 1, limit: 100 }) as any)
    dispatch(getClientRoles({ page: 1, limit: 100 }) as any)
  }, [dispatch])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, roleId, departmentId, teamId, mode])

  useEffect(() => {
    const params: any = { page, limit: PAGE_LIMIT }
    const q = debouncedSearch.trim()
    if (q) params.q = q
    if (roleId) params.roleIds = String(roleId)
    dispatch(getUsers(params))
  }, [dispatch, page, debouncedSearch, roleId])

  const rows = useMemo<Row[]>(() => (employees ?? []).map(toRow), [employees])

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (departmentId != null && r.departmentId !== departmentId) return false
      if (teamId != null && r.teamId !== teamId) return false
      return true
    })
  }, [rows, departmentId, teamId])

  const allVisibleSelected =
    filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id))

  const toggle = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChangeSelected(next)
  }

  const toggleAllVisible = () => {
    const next = new Set(selected)
    if (allVisibleSelected) {
      filteredRows.forEach((r) => next.delete(r.id))
    } else {
      filteredRows.forEach((r) => next.add(r.id))
    }
    onChangeSelected(next)
  }

  const departmentOptions = useMemo<Option[]>(
    () => (departments ?? []).map((d: any) => ({ label: d.name, value: d.id })),
    [departments],
  )
  const roleOptions = useMemo<Option[]>(
    () => (clientRoles ?? []).map((r: any) => ({ label: r.name, value: r.id })),
    [clientRoles],
  )
  const teamOptions = useMemo<Option[]>(
    () => simpleTeams.map((t) => ({ label: t.name, value: t.id })),
    [simpleTeams],
  )

  const findOption = (opts: Option[], v: number | null): Option | null =>
    v == null ? null : opts.find((o) => o.value === v) ?? null
  const nullDept: Option = { label: 'All departments', value: null }
  const nullRole: Option = { label: 'All roles', value: null }
  const nullTeam: Option = { label: 'All teams', value: null }
  const toNumOrNull = (v: Option['value']): number | null =>
    v == null ? null : (v as number)

  return (
    <>
      <ModePickerRow>
        <ModeCard
          type='button'
          active={mode === 'filter'}
          onClick={() => onModeChange('filter')}
        >
          <ModeCardIcon active={mode === 'filter'}>{FilterIconSvg}</ModeCardIcon>
          <ModeCardTitle active={mode === 'filter'}>Filter Based</ModeCardTitle>
          <ModeCardSubtitle>
            Select by department, role or current team
          </ModeCardSubtitle>
        </ModeCard>
        <ModeCard
          type='button'
          active={mode === 'manual'}
          onClick={() => onModeChange('manual')}
        >
          <ModeCardIcon active={mode === 'manual'}>{ManualIconSvg}</ModeCardIcon>
          <ModeCardTitle active={mode === 'manual'}>Manual Select</ModeCardTitle>
          <ModeCardSubtitle>Pick employees individually from list</ModeCardSubtitle>
        </ModeCard>
        <ModeCard
          type='button'
          active={mode === 'import'}
          onClick={() => onModeChange('import')}
        >
          <ModeCardIcon active={mode === 'import'}>{ImportIconSvg}</ModeCardIcon>
          <ModeCardTitle active={mode === 'import'}>Import List</ModeCardTitle>
          <ModeCardSubtitle>Paste employees ID or number</ModeCardSubtitle>
        </ModeCard>
      </ModePickerRow>

      {mode === 'manual' ? (
        <FiltersBar>
          <SearchField>
            <NavBarSearchIcon />
            <input
              type='text'
              aria-label='Search for employee'
              placeholder='Search for employee'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchField>
          <Select
            placeholder='Employees'
            options={departmentOptions}
            nullValue={nullDept}
            value={findOption(departmentOptions, departmentId)}
            onChange={(opt) => setDepartmentId(toNumOrNull(opt.value))}
          />
          <Select
            placeholder='All teams'
            options={teamOptions}
            nullValue={nullTeam}
            value={findOption(teamOptions, teamId)}
            onChange={(opt) => setTeamId(toNumOrNull(opt.value))}
          />
          <CalendarInput
            width='100%'
            placeholder='Date range'
            range
            nomarg
            defaultValue={
              rangeStart && rangeEnd
                ? [rangeStart, rangeEnd]
                : undefined
            }
            onChange={(...args: any[]) => {
              const [first, last] = args
              if (first instanceof Date) setRangeStart(first)
              if (last instanceof Date) setRangeEnd(last)
            }}
          />
          <FilterIconButton type='button' title='More filters' aria-label='More filters'>
            <FilterIcon />
          </FilterIconButton>
        </FiltersBar>
      ) : mode === 'filter' ? (
        <SimpleFiltersBar>
          <SearchField>
            <NavBarSearchIcon />
            <input
              type='text'
              aria-label='Search for employee'
              placeholder='Search for employee'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchField>
          <Select
            placeholder='All departments'
            options={departmentOptions}
            nullValue={nullDept}
            value={findOption(departmentOptions, departmentId)}
            onChange={(opt) => setDepartmentId(toNumOrNull(opt.value))}
          />
          <Select
            placeholder='All roles'
            options={roleOptions}
            nullValue={nullRole}
            value={findOption(roleOptions, roleId)}
            onChange={(opt) => setRoleId(toNumOrNull(opt.value))}
          />
          <Select
            placeholder='All teams'
            options={teamOptions}
            nullValue={nullTeam}
            value={findOption(teamOptions, teamId)}
            onChange={(opt) => setTeamId(toNumOrNull(opt.value))}
          />
          <FilterIconButton type='button' title='More filters' aria-label='More filters'>
            <FilterIcon />
          </FilterIconButton>
        </SimpleFiltersBar>
      ) : null}

      {mode === 'import' && (
        <div style={{ padding: '1rem 0' }}>
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            CSV import is not yet available — please use Filter Based or Manual Select.
          </SmallText>
        </div>
      )}

      {mode !== 'import' && (
        <>
          <EmployeeListWrapper>
            <EmployeeListHeader>
              <Checkbox checked={allVisibleSelected} />
              <span>
                EMPLOYEES MATCH ({selected.size} SELECTED)
              </span>
              <SelectAllButton type='button' onClick={toggleAllVisible}>
                {allVisibleSelected ? 'Deselect all' : 'Select all'}
              </SelectAllButton>
            </EmployeeListHeader>

            {filteredRows.length === 0 ? (
              <div style={{ padding: '1.5rem 1rem' }}>
                <SmallText colorVariant={ColorVariant.SECONDARY}>
                  {isLoading ? 'Loading…' : 'No employees match these filters.'}
                </SmallText>
              </div>
            ) : (
              filteredRows.map((r) => {
                const isSel = selected.has(r.id)
                return (
                  <EmployeeRow key={r.id} selected={isSel} onClick={() => toggle(r.id)}>
                    <Checkbox checked={isSel} />
                    <div>{r.name}</div>
                    <div style={{ color: tokens.text.secondary }}>{r.empId}</div>
                    <div>{r.roleName}</div>
                    <div>{r.departmentName}</div>
                    <TeamChip>{r.teamName ?? '—'}</TeamChip>
                  </EmployeeRow>
                )
              })
            )}
          </EmployeeListWrapper>

          {totalPages > 1 && (
            <Flex justcenter mt='16'>
              <Pagination pageCount={totalPages} setPage={setPage} forcePage={page - 1} />
            </Flex>
          )}
        </>
      )}
    </>
  )
}
